/**
 * Structured API error strings for UX (avoid raw Axios noise).
 */

import type { AxiosError } from 'axios'

async function messageFromResponseData(data: unknown): Promise<string | null> {
  if (data instanceof Blob) {
    try {
      const text = await data.text()
      const parsed = JSON.parse(text) as { message?: string; error?: string }
      if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message
      if (typeof parsed.error === 'string' && parsed.error.trim()) return parsed.error
    } catch {
      return null
    }
    return null
  }

  const body = data as { message?: string; error?: string } | undefined
  if (typeof body?.message === 'string' && body.message.trim()) return body.message
  if (typeof body?.error === 'string' && body.error.trim()) return body.error
  return null
}

export async function getApiErrorMessageAsync(
  error: unknown,
  fallback = 'Something went wrong. Please try again.'
): Promise<string> {
  const ax = error as AxiosError<unknown>
  const fromBody = await messageFromResponseData(ax?.response?.data)
  if (fromBody) return fromBody
  return getApiErrorMessage(error, fallback)
}

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const ax = error as AxiosError<{ message?: string; error?: string }>

  const status = ax?.response?.status

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You appear to be offline. Please check your connection.'
  }

  const code = (error as AxiosError)?.code
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'Unable to reach the server. Please check your connection and retry.'
  }

  const body = ax?.response?.data
  if (body instanceof Blob) {
    return fallback
  }
  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message
  }
  if (typeof body?.error === 'string' && body.error.trim()) {
    return body.error
  }
  if (typeof ax.message === 'string' && ax.message.includes('timeout')) {
    return 'Request timed out. Please try again.'
  }
  if (typeof status === 'number') {
    if (status >= 500) return 'Something went wrong on our side. Please try again in a moment.'
    if (status === 408 || status === 504) return 'Request timed out. Please try again.'
    if (status === 429) return 'Too many requests. Please wait and retry.'
  }

  return fallback
}
